
import { useState, useEffect, useCallback } from "react";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";

export interface SmsTemplate {
  id: string;
  template_type: 'order_confirmation' | 'order_ready' | 'ask_for_reviews';
  message_text: string;
  promotional_append?: string | null;
  is_active: boolean;
  word_count: number;
  character_count: number;
  created_at: string;
  updated_at: string;
}

export const useSmsTemplates = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sms_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching SMS templates:', error);
        setError(error.message);
        toast({
          title: "Error",
          description: "Failed to fetch SMS templates",
          variant: "destructive",
        });
        return;
      }

      setTemplates((data || []) as SmsTemplate[]);
      setError(null);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient, toast]);

  const createTemplate = async (templateData: {
    template_type: SmsTemplate['template_type'];
    message_text: string;
    promotional_append?: string | null;
    is_active?: boolean;
  }) => {
    const supabase = await getSupabaseClient();
    if (!supabase) throw new Error("Supabase client not initialized.");

    try {
      const { data, error } = await supabase
        .from('sms_templates')
        .insert([templateData as any])
        .select()
        .single();

      if (error) {
        console.error('Error creating SMS template:', error);
        toast({
          title: "Error",
          description: "Failed to create SMS template",
          variant: "destructive",
        });
        throw error;
      }

      setTemplates(prev => [data as SmsTemplate, ...prev]);
      toast({
        title: "Template Created",
        description: "SMS template has been created successfully",
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      throw err;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<SmsTemplate>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) throw new Error("Supabase client not initialized.");

    try {
      const { data, error } = await supabase
        .from('sms_templates')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating SMS template:', error);
        toast({
          title: "Error",
          description: "Failed to update SMS template",
          variant: "destructive",
        });
        throw error;
      }

      setTemplates(prev => prev.map(template =>
        template.id === id ? { ...template, ...data } as SmsTemplate : template
      ));

      toast({
        title: "Template Updated",
        description: "SMS template has been updated successfully",
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      throw err;
    }
  };

  const deleteTemplate = async (id: string) => {
    const supabase = await getSupabaseClient();
    if (!supabase) throw new Error("Supabase client not initialized.");

    try {
      const { error } = await supabase
        .from('sms_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting SMS template:', error);
        toast({
          title: "Error",
          description: "Failed to delete SMS template",
          variant: "destructive",
        });
        throw error;
      }

      setTemplates(prev => prev.filter(template => template.id !== id));
      toast({
        title: "Template Deleted",
        description: "SMS template has been deleted successfully",
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refetchTemplates: fetchTemplates
  };
};
